import type { HouseholdState, InventoryItem, RecipeSuggestion, SavedRecipe, SavedRecipeIngredient } from '../types';

function norm(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim().replace(/\s+/g, ' ');
}

function singular(value: string) {
  return value.split(' ').map((part) => {
    if (part.endsWith('ies') && part.length > 4) return `${part.slice(0, -3)}y`;
    if (part.endsWith('oes') && part.length > 4) return part.slice(0, -2);
    if (part.endsWith('s') && !part.endsWith('ss') && part.length > 3) return part.slice(0, -1);
    return part;
  }).join(' ');
}

function namesMatch(a: string, b: string) {
  return singular(norm(a)) === singular(norm(b));
}

type BaseQuantity = { dimension: 'mass'|'volume'|'count'|'unit'; unit: string; quantity: number };

function baseQuantity(quantity: number, unit: string): BaseQuantity {
  const u = norm(unit);
  if (u === 'kg' || u === 'kilogram' || u === 'kilograms') return { dimension: 'mass', unit: 'g', quantity: quantity * 1000 };
  if (u === 'g' || u === 'gram' || u === 'grams') return { dimension: 'mass', unit: 'g', quantity };
  if (u === 'lb' || u === 'lbs' || u === 'pound' || u === 'pounds') return { dimension: 'mass', unit: 'g', quantity: quantity * 453.59237 };
  if (u === 'oz' || u === 'ounce' || u === 'ounces') return { dimension: 'mass', unit: 'g', quantity: quantity * 28.349523125 };
  if (u === 'l' || u === 'liter' || u === 'litre' || u === 'liters' || u === 'litres') return { dimension: 'volume', unit: 'ml', quantity: quantity * 1000 };
  if (u === 'ml' || u === 'milliliter' || u === 'millilitre' || u === 'milliliters' || u === 'millilitres') return { dimension: 'volume', unit: 'ml', quantity };
  if (['pcs','pc','piece','pieces','ea','each','ct','count'].includes(u)) return { dimension: 'count', unit: 'pcs', quantity };
  return { dimension: 'unit', unit: u, quantity };
}

function hasEnough(ingredient: SavedRecipeIngredient, inventory: InventoryItem[]) {
  const candidates = inventory.filter((item) => item.quantity > 0 && namesMatch(item.name, ingredient.name));
  if (!candidates.length) return false;
  const need = baseQuantity(ingredient.quantity, ingredient.unit);
  let available = 0;
  for (const item of candidates) {
    const have = baseQuantity(item.quantity, item.unit);
    if (have.dimension !== need.dimension || have.unit !== need.unit) continue;
    available += have.quantity;
  }
  return available + 1e-6 >= need.quantity;
}

function daysSince(value?: string | null) {
  if (!value) return Number.POSITIVE_INFINITY;
  return Math.max(0, (Date.now() - new Date(value).getTime()) / 86400000);
}

export function getMakeAgainSuggestions(state: HouseholdState, recipes: SavedRecipe[], limit = 3): RecipeSuggestion[] {
  if (!recipes.length || !state.inventory.length) return [];
  const now = Date.now();
  const alreadyPlanned = new Set(state.meals.filter((meal) => meal.status !== 'cooked' && meal.status !== 'skipped').map((meal) => singular(norm(meal.name))));

  return recipes.map((recipe) => {
    if (recipe.dismissedUntil && new Date(recipe.dismissedUntil).getTime() > now) return null;
    if (daysSince(recipe.lastMadeAt) < 7) return null;
    if (daysSince(recipe.lastSuggestedAt) < 4) return null;
    if (alreadyPlanned.has(singular(norm(recipe.name)))) return null;
    const required = recipe.ingredients.filter((ingredient) => !ingredient.optional);
    if (!required.length) return null;
    const matched = required.filter((ingredient) => hasEnough(ingredient, state.inventory));
    const missing = required.filter((ingredient) => !hasEnough(ingredient, state.inventory)).map((ingredient) => ingredient.name);
    const coverage = matched.length / required.length;
    if (coverage < 0.7) return null;
    const recencyPenalty = Number.isFinite(daysSince(recipe.lastMadeAt)) ? Math.max(0, 14 - daysSince(recipe.lastMadeAt)) : 0;
    const score = coverage * 100 + (recipe.favorite ? 8 : 0) + Math.min(recipe.timesMade, 5) * 1.5 - recencyPenalty;
    const reason = coverage === 1
      ? `You already have all ${required.length} tracked ingredients for this.`
      : `You already have ${matched.length} of ${required.length} tracked ingredients. Missing: ${missing.slice(0, 2).join(', ')}${missing.length > 2 ? '…' : ''}`;
    return { recipe, coverage, matchedIngredients: matched.length, totalIngredients: required.length, missingIngredients: missing, reason, score };
  }).filter((value): value is RecipeSuggestion & { score: number } => Boolean(value)).sort((a, b) => b.score - a.score).slice(0, limit).map(({ score: _score, ...suggestion }) => suggestion);
}
